import { PendingState, ProcessState } from '@/components/interactions/cart/add-to-cart/process-state'
import AddItemToCart, { AddItemToCartResult } from '@/models/cart/command/add-item-to-cart'
import { FetchSkuPrice } from '@/models/item/sku/repository/fetch-sku-price'
import { DefaultFetchSkuPrice } from '@/models/item/sku/repository/impl/fetch-sku-price'
import AppId from '@/util/appid'
import { DataLayerService } from '@/vendor/gtm/data-layer'
import { addToCartEventFactory } from '@/vendor/gtm/events/ga4/add-to-cart'
import { CartService } from '@ajax/modules/services/cart'
import { ProductService as AjaxProductService } from '@ajax/modules/services/product'
import { Either, left, right } from 'fp-ts/lib/Either'

interface AddToCartBrandDependencies {
  addItemToCartCommand: AddItemToCart
  fetchSkuPrice: FetchSkuPrice
  pushEventToDataLayer: (results: AddItemToCartResult[]) => void
}
export class AddToCartBrandDependenciesFactory {
  create(brandName: string): AddToCartBrandDependencies {
    return {
      addItemToCartCommand: new AddItemToCart(
        CartService.createFromBrandEnglishName(brandName),
        new AjaxProductService(AppId.getByBrandName(brandName))
      ),
      fetchSkuPrice: DefaultFetchSkuPrice.create(brandName),
      pushEventToDataLayer: (results) => {
        new DataLayerService().push(addToCartEventFactory.createFromAddItemToCartResults(results))
      },
    }
  }
}

/**
 * カート追加処理の状態を管理する
 *
 * ひとつのページに複数のカート追加ボタンを配置する場合などに、ページ全体の状態を管理するのが目的
 * - 各ボタンコンポーネントが、ページ全体として処理が実行中であるか知ることができる
 * - カート追加処理が実行中の場合、他の処理開始をブロックする
 *
 * 一方で、カート追加の結果はこのクラスでは管理しない
 */
export class AddToCartProcessStateStore {
  static init(): AddToCartProcessStateStore {
    return new AddToCartProcessStateStore(
      new PendingState(),
      new AddToCartBrandDependenciesFactory()
    )
  }

  /**
   * @internal テスト時のみ利用可。通常時は AddToCartProcessStateStore.init() を通してインスタンスを作成すること
   */
  constructor(
    public processState: ProcessState,
    private readonly addToCartBrandDependenciesFactory: AddToCartBrandDependenciesFactory
  ) {}

  /**
   * 他のカート追加処理が実行中でなければカート追加処理を開始する
   */
  async start(
    brandName: string,
    skuId: string,
    count: number
  ): Promise<Either<AddToCartError, AddToCartProcessStateStore>> {
    if (!this.processState.isPending()) {
      return left({
        error: new Error(`process state must be pending`),
        displayMessages: ['他の商品をカートに追加中のため、この操作はできません。完了してからもう一度お試しください。'],
      })
    }

    const dependencies = this.addToCartBrandDependenciesFactory.create(brandName)
    this.processState = this.processState.start()

    const skuPrice = await dependencies.fetchSkuPrice.fetchById(skuId)
    if (skuPrice.isLeft()) {
      this.processState = this.processState.reset()

      return left({
        error: skuPrice.value,
        // 価格取得失敗であることをユーザーに伝える必要はないので
        displayMessages: ['カートに追加できませんでした。'],
      })
    }

    const result = await dependencies.addItemToCartCommand.execute({
      productId: skuId,
      count,
      price: skuPrice.value,
    })
    this.processState = this.processState.reset()

    return result.fold(
      errorMessage => left({
        error: new Error('add item to cart command failed'),
        displayMessages: [errorMessage],
      }),
      (addItemToCartResult) => {
        dependencies.pushEventToDataLayer([addItemToCartResult])

        return right(this)
      }
    )
  }
}

interface AddToCartError {
  error: Error
  displayMessages: string[]
}
